import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Video, Plus, Pencil, Trash2, Loader2, Building2 } from 'lucide-react';

const TIPI_IMPIANTO = [
  { value: 'sonorizzazione', label: 'Sonorizzazione' },
  { value: 'videoproiezione', label: 'Videoproiezione' },
  { value: 'led_wall', label: 'LED Wall' },
  { value: 'streaming', label: 'Streaming / Regia' },
  { value: 'illuminotecnica', label: 'Illuminotecnica' },
  { value: 'congressuale', label: 'Sistema Congressuale' },
  { value: 'altro', label: 'Altro' },
];

export default function ImpiantiAudiovisiviPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nome: '', tipo: 'sonorizzazione', cliente: '', evento: '',
    data_evento: '', luogo: '', importo: '', stato: 'preventivo', note: ''
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/studio/impianti-audiovisivi');
      setItems(r.data || []);
    } catch (e) {
      // Endpoint potrebbe non esistere ancora — gestiamo gracefully
      setItems([]);
    } finally { setLoading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, importo: parseFloat(form.importo || '0') || 0 };
      if (editing) {
        await api.put(`/studio/impianti-audiovisivi/${editing.id}`, payload);
        toast.success('Aggiornato');
      } else {
        await api.post('/studio/impianti-audiovisivi', payload);
        toast.success('Creato');
      }
      setOpen(false); setEditing(null);
      setForm({ nome: '', tipo: 'sonorizzazione', cliente: '', evento: '', data_evento: '', luogo: '', importo: '', stato: 'preventivo', note: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSubmitting(false); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nome: item.nome || '', tipo: item.tipo || 'sonorizzazione',
      cliente: item.cliente || '', evento: item.evento || '',
      data_evento: item.data_evento || '', luogo: item.luogo || '',
      importo: String(item.importo || ''), stato: item.stato || 'preventivo',
      note: item.note || '',
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo impianto?')) return;
    try { await api.delete(`/studio/impianti-audiovisivi/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error('Errore eliminazione'); }
  };

  const statoBadge = (s) => {
    const map = {
      preventivo: 'bg-amber-100 text-amber-800',
      confermato: 'bg-blue-100 text-blue-800',
      installato: 'bg-emerald-100 text-emerald-800',
      fatturato: 'bg-violet-100 text-violet-800',
      archiviato: 'bg-slate-100 text-slate-600',
    };
    return map[s] || 'bg-slate-100';
  };

  return (
    <div className="space-y-6" data-testid="impianti-av-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Video className="w-7 h-7 text-indigo-600" /> Impianti Audiovisivi
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Gestione preventivi, installazioni e fatturazione di impianti A/V per eventi.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-impianto-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Impianto
        </Button>
      </div>

      {loading && <div className="text-center py-8 text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Caricamento…</div>}

      {!loading && items.length === 0 && (
        <Card className="bg-slate-50">
          <CardContent className="py-10 text-center text-slate-500">
            <Video className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Nessun impianto in elenco</p>
            <p className="text-sm mt-1">Click "Nuovo Impianto" per iniziare.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {items.map((it) => (
          <Card key={it.id} data-testid={`impianto-${it.id}`} className="border-l-4 border-l-indigo-400">
            <CardContent className="py-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={statoBadge(it.stato)}>{it.stato}</Badge>
                    <span className="font-semibold text-slate-800">{it.nome}</span>
                    <Badge variant="outline">{TIPI_IMPIANTO.find(t => t.value === it.tipo)?.label || it.tipo}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1"><Building2 className="w-3.5 h-3.5 inline mr-1" /> {it.cliente}</p>
                  {it.evento && <p className="text-xs text-slate-500">📅 {it.evento} • {it.data_evento} • {it.luogo}</p>}
                  {it.importo > 0 && <p className="text-sm font-bold text-emerald-700">💰 €{it.importo.toFixed(2)}</p>}
                  {it.note && <p className="text-xs text-slate-500 italic mt-1">{it.note}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(it.id)} className="text-red-600" data-testid={`del-${it.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Impianto' : 'Nuovo Impianto'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Nome impianto *</Label><Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} placeholder="Es: Sonorizzazione Sala A" data-testid="form-nome" /></div>
            <div>
              <Label>Tipologia</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                <SelectTrigger data-testid="form-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI_IMPIANTO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cliente</Label><Input value={form.cliente} onChange={e => setForm({...form, cliente: e.target.value})} data-testid="form-cliente" /></div>
            <div><Label>Evento</Label><Input value={form.evento} onChange={e => setForm({...form, evento: e.target.value})} data-testid="form-evento" /></div>
            <div><Label>Data evento</Label><Input type="date" value={form.data_evento} onChange={e => setForm({...form, data_evento: e.target.value})} data-testid="form-data" /></div>
            <div><Label>Luogo</Label><Input value={form.luogo} onChange={e => setForm({...form, luogo: e.target.value})} data-testid="form-luogo" /></div>
            <div><Label>Importo €</Label><Input type="number" step="0.01" value={form.importo} onChange={e => setForm({...form, importo: e.target.value})} data-testid="form-importo" /></div>
            <div>
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({...form, stato: v})}>
                <SelectTrigger data-testid="form-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="preventivo">Preventivo</SelectItem>
                  <SelectItem value="confermato">Confermato</SelectItem>
                  <SelectItem value="installato">Installato</SelectItem>
                  <SelectItem value="fatturato">Fatturato</SelectItem>
                  <SelectItem value="archiviato">Archiviato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={3} value={form.note} onChange={e => setForm({...form, note: e.target.value})} data-testid="form-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={submitting || !form.nome} className="bg-indigo-600 hover:bg-indigo-700" data-testid="form-save">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
